import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  setEmailAiDraftContent,
  setEmailAiResponseHistoryId,
  setTextAiDraftContent,
  setTextAiResponseHistoryId,
} from "../../../../redux/slices/aiSlice";
import { AI_CHANNEL } from "./constants";

const CHANNEL_ACTIONS = {
  [AI_CHANNEL.TEXT]: {
    setHistoryId: setTextAiResponseHistoryId,
    setDraftContent: setTextAiDraftContent,
  },
  [AI_CHANNEL.EMAIL]: {
    setHistoryId: setEmailAiResponseHistoryId,
    setDraftContent: setEmailAiDraftContent,
  },
};

/**
 * Undo history for a composer whose content an AI can overwrite (EAI-1006).
 *
 * Pair with `useAiDraftWriter`: give its `onDraftReplaced` to `record`, and the
 * before/after of every refine lands here. Render the result with
 * `DraftVersionControls`, or drive your own UI from the returned state.
 *
 * Entries are AI output only — page 1 is the first refined message, not what the
 * user typed. The pre-refine message is held separately and restored by Undo.
 *
 * Each entry is `{ content, subject, historyId }`. `content` is in whatever form
 * the composer restores from — HTML for the rich email editor, plain text for a
 * textarea — not the plain text that was sent to the API, so formatting and line
 * breaks survive a step back. `subject` is only set on
 * channels that have one (email), so Undo puts body and subject back together.
 * `historyId` is the getAiResponse id that produced that text, or null when the
 * text is the user's own — the draft feedback thumbs key off it, so it has to
 * travel with the version rather than be read back from redux, which only ever
 * holds the most recent response.
 *
 * @param {{
 *   channel?: string,                 // an AI_CHANNEL; picks the redux slot to sync
 *   adapter: object,                  // see draftAdapters.js — used to put content back
 *   versioning?: boolean,             // false = record nothing, expose no history
 *   versionRewrites?: boolean,        // true = a plain generate joins the history too
 * }} config
 *
 * `versioning: false` keeps the hook call in place (hooks can't be conditional)
 * while turning every action into a no-op and reporting no history, so a surface
 * that doesn't want versioning costs one unused state pair and renders no
 * controls — `DraftVersionControls` hides itself on `hasHistory: false`.
 *
 * `versionRewrites` is the narrower switch, and it is off everywhere today.
 * Generate means "I don't like this one, write me another", so by default it
 * ends the chain rather than extending it — there is nothing to step back
 * through, because the user asked for a replacement, not a revision. Turn it on
 * for a surface where a generate should be just another version, and Undo and
 * the pager then cover both actions alike.
 */
export default function useDraftVersions({
  channel = AI_CHANNEL.TEXT,
  adapter,
  versioning = true,
  versionRewrites = false,
}) {
  const dispatch = useDispatch();
  const actions = CHANNEL_ACTIONS[channel] || CHANNEL_ACTIONS[AI_CHANNEL.TEXT];

  // Only AI output lives in `versions` — page 1 is the first refined message.
  // What the user wrote before the first refine is kept aside in `original`: it
  // is not a page, and comes back only through Undo.
  const [versions, setVersions] = useState([]);
  const [index, setIndex] = useState(-1);
  const [original, setOriginal] = useState(null);

  const reset = () => {
    setVersions([]);
    setIndex(-1);
    setOriginal(null);
  };

  /**
   * Wire this to `useAiDraftWriter`'s `onDraftReplaced`.
   *
   * Only a refine builds history, unless `versionRewrites` says otherwise.
   * Generate means "I don't like this one, write me another", so by default it
   * ends the chain instead of extending it: no Undo, no pager, whether or not
   * the composer happened to hold a previous draft. That also stops a pager
   * surviving a Generate and offering versions whose text is no longer anywhere
   * in the composer.
   */
  const record = ({
    isRefine = true,
    source,
    sourceSubject,
    sourceHistoryId,
    result,
    resultSubject,
    resultHistoryId,
  }) => {
    if (!versioning) return;
    if (!isRefine && !versionRewrites) {
      reset();
      return;
    }
    setVersions(prev => {
      // Refining from anywhere but the newest version starts a new branch, so
      // drop what came after it — the same thing an editor does when you type
      // after undoing. Without this, stepping back to v1 and refining would
      // leave the old v2 stranded after the new one.
      const kept = index >= 0 ? prev.slice(0, index + 1) : [];

      // The composer is uncontrolled, so the user can edit whatever they landed
      // on before refining again. `source` is what was actually in the box, which
      // is the truth — otherwise stepping back would hand them the pre-edit text
      // and lose their words.
      const current = { content: source, subject: sourceSubject, historyId: sourceHistoryId };
      if (kept.length > 0) {
        // Refining an AI version: update that page in place.
        kept[kept.length - 1] = current;
      } else if (source) {
        // First refine of this chain — the source is the user's own message, so
        // it is stashed for Undo instead of becoming page 1.
        setOriginal(current);
      }

      const next = [
        ...kept,
        { content: result, subject: resultSubject, historyId: resultHistoryId },
      ];
      setIndex(next.length - 1);
      return next;
    });
  };

  // Put a stored version back into the composer and re-point the draft-feedback
  // feature at it. Without the redux part the feature still describes the newest
  // draft, so stepping back looks like the user hand-edited Otto's text and the
  // thumbs vanish — and a rating given there would post against the wrong
  // aiResponseHistoryId. A null id is the user's own text, which isn't Otto's to
  // rate; the reducer drops the stored draft content along with the id.
  const applyVersion = version => {
    adapter.restoreDraft(version.content);
    adapter.writeSubject?.(version.subject);
    dispatch(actions.setHistoryId(version.historyId));
    if (version.historyId) dispatch(actions.setDraftContent(version.content));
  };

  const goTo = idx => {
    if (!versioning) return;
    const version = versions[idx];
    if (!version) return;
    applyVersion(version);
    setIndex(idx);
  };

  /**
   * Undo is all the way back, not one step: it restores the message the user
   * wrote before any refining and throws the history away. Refine is a thing you
   * either keep or abandon, so "give me my message back" is the action people
   * actually want — stepping between AI drafts is what the pager is for.
   */
  const undo = () => {
    if (!versioning || !original) return;
    applyVersion(original);
    reset();
  };

  return {
    versioning,
    versions: versioning ? versions : [],
    index,
    record,
    goTo,
    reset,
    undo,
    original,
    /**
     * Available as soon as one refine has happened, independent of the pager —
     * a single refine produces one page but is very much undoable.
     */
    canUndo: versioning && !!original,
    /** The pager only earns its place once there are two AI versions to move between. */
    hasHistory: versioning && versions.length >= 2,
  };
}
